/**
 * Cart + checkout journey integration.
 *
 * Observes Shopify cart changes without wrapping `window.fetch`. The previous
 * implementation called the cart endpoint twice and patched fetch globally,
 * which was unsafe alongside the theme's quantity reconciler and was therefore
 * never loaded. This version keeps an authoritative /cart.js snapshot, diffs
 * successful mutations, and emits both GA4 ecommerce events and the first-party
 * journey events consumed by AIMarketOps.
 */
(function () {
  'use strict';

  var previous = null;
  var syncing = false;
  var syncAgain = false;
  var syncTimer = null;
  var lastFingerprint = '';

  function itemKey(item) {
    return String(item.key || item.id || item.variant_id || '');
  }

  function fingerprint(cart) {
    return (cart.items || [])
      .map(function (item) { return itemKey(item) + ':' + Number(item.quantity || 0); })
      .sort()
      .join('|');
  }

  function itemMap(cart) {
    var map = {};
    (cart.items || []).forEach(function (item) { map[itemKey(item)] = item; });
    return map;
  }

  function ecommerce() {
    return window.KT && window.KT.EcommerceEvents;
  }

  function journey(name, data) {
    if (window.KTJourneyTracker && typeof window.KTJourneyTracker.track === 'function') {
      window.KTJourneyTracker.track(name, data);
    }
  }

  function emitDelta(before, after) {
    if (!before) return;
    var beforeItems = itemMap(before);
    var afterItems = itemMap(after);
    var keys = {};
    Object.keys(beforeItems).forEach(function (key) { keys[key] = true; });
    Object.keys(afterItems).forEach(function (key) { keys[key] = true; });

    Object.keys(keys).forEach(function (key) {
      var oldItem = beforeItems[key];
      var newItem = afterItems[key];
      var oldQuantity = Number(oldItem && oldItem.quantity || 0);
      var newQuantity = Number(newItem && newItem.quantity || 0);
      var delta = newQuantity - oldQuantity;
      if (!delta) return;

      var item = newItem || oldItem;
      var eventItem = Object.assign({}, item, { quantity: Math.abs(delta) });
      var tracker = ecommerce();
      if (delta > 0 && tracker && typeof tracker.addToCart === 'function') {
        tracker.addToCart(eventItem, delta);
      } else if (delta < 0 && tracker && typeof tracker.removeFromCart === 'function') {
        tracker.removeFromCart(eventItem);
      }
      // journey-tracker.liquid owns kt_journey_add_to_cart: its cart:updated
      // listener is the earliest reliable success signal and already covers this
      // mutation. Emitting it here as well stored two raw KT add events for one
      // customer action. Keep removal here because journey-tracker has no removal
      // observer; the GA4 ecommerce add/remove calls above remain unchanged.
      if (delta < 0) {
        journey('remove_from_cart', {
          variant_id: String(item.variant_id || item.id || ''),
          quantity: Math.abs(delta),
          cart_item_count: Number(after.item_count || 0),
          cart_total: Number(after.total_price || 0),
          source_page: window.location.pathname
        });
      }
    });
  }

  function reconcile() {
    if (syncing) { syncAgain = true; return; }
    syncing = true;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (response) { return response.ok ? response.json() : Promise.reject(new Error('cart_read_failed')); })
      .then(function (cart) {
        var nextFingerprint = fingerprint(cart);
        if (nextFingerprint !== lastFingerprint) emitDelta(previous, cart);
        previous = cart;
        lastFingerprint = nextFingerprint;
      })
      .catch(function () { /* analytics must never affect the storefront */ })
      .finally(function () {
        syncing = false;
        if (syncAgain) { syncAgain = false; reconcile(); }
      });
  }

  function scheduleReconcile() {
    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = setTimeout(reconcile, 120);
  }

  function observeCartRequests() {
    if (typeof PerformanceObserver !== 'function') return;
    try {
      var CART_MUTATION = /\/cart\/(?:add|change|update)(?:\.js)?(?:\?|$)/;
      new PerformanceObserver(function (list) {
        var entries = list.getEntries();
        for (var i = 0; i < entries.length; i += 1) {
          if (CART_MUTATION.test(entries[i].name)) { scheduleReconcile(); return; }
        }
      }).observe({ type: 'resource', buffered: true });
    } catch (error) {
      // The theme's cart events remain available when Resource Timing is absent.
    }
  }

  // Fires on the click that LEAVES the cart, so it measures intent to check out
  // — not arrival at checkout. Those are different numbers and the gap between
  // them is real: a shopper can click and never land, via a slow redirect, a
  // blocked third party, or simply changing their mind on the way.
  //
  // This used to emit `begin_checkout`, which made the checkout stage of the
  // funnel unreliable by counting non-arrivals in the numerator. `begin_checkout`
  // is now owned by extensions/kt-checkout-funnel-pixel, which subscribes to
  // Shopify's `checkout_started` inside the checkout sandbox and therefore only
  // fires for shoppers who actually got there. Keep these two names distinct.
  function checkoutIntent(event) {
    var target = event.target && event.target.closest
      ? event.target.closest('button[name="checkout"], a[href*="/checkout"], [data-checkout]')
      : null;
    if (!target) return;
    journey('kt_journey_checkout_intent', {
      source_page: window.location.pathname,
      cart_item_count: previous ? Number(previous.item_count || 0) : 0,
      cart_total: previous ? Number(previous.total_price || 0) : 0
    });
  }

  function bootstrap() {
    reconcile();
    observeCartRequests();
    document.addEventListener('cart:updated', scheduleReconcile);
    document.addEventListener('cart:add', scheduleReconcile);
    document.addEventListener('cart:remove', scheduleReconcile);
    document.addEventListener('click', checkoutIntent, true);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootstrap);
  else bootstrap();
})();
